import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { ProductListService } from './product-list.service';
import { FooterComponent } from '../../../components/footer/footer.component';
import { HeaderComponent } from '../../../components/header/header.component';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ProductListItemComponent } from './product-list-item/product-list-item.component';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { PaginatorComponent } from '../../../components/paginator/paginator.component';
import { map, tap } from 'rxjs';
import { SlideInComponent } from '../../../components/slide-in/slide-in.component';
import { FiltersComponent } from './filters/filters.component';
import { BackdropComponent } from '../../../components/backdrop/backdrop.component';
import { ButtonComponent } from '../../../components/button/button.component';
import { Filters } from '../../../types/filters.type';
import { FiltersService, getSelectedFiltersAsParams } from './filters.service';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { DropdownComponent } from '../../../components/dropdown/dropdown.component';
import { DropdownOption } from '../../../components/product/product.types';
import { SortOption } from './sort.type';
import { AddLocalePipe } from '../../../pipes/url-locale.pipe';
import { ProductListHeaderComponent } from './product-list-header/product-list-header.component';
import { SeoService } from '../../../services/seo.service';
import { DataLayerService } from '../../../services/data-layer.service';
import { ProductListItemData } from './product-list.types';
import { ROUTE_TRANSLATIONS } from '../../../app.routes';
import { StateService } from '../../../services/state.service';
import { PAGE_SIZE_DEFAULT, SORT_OPTION_DEFAULT } from './product-list.options';

const EMPTY_FILTERS = {
  optionTypes: [],
  productTypes: [],
} as Filters;

@Component({
  selector: 'app-product-list',
  imports: [
    FooterComponent,
    HeaderComponent,
    RouterLink,
    ProductListItemComponent,
    PaginatorComponent,
    SlideInComponent,
    FiltersComponent,
    BackdropComponent,
    ButtonComponent,
    TranslocoDirective,
    SvgIconComponent,
    DropdownComponent,
    AddLocalePipe,
    ProductListHeaderComponent,
  ],
  templateUrl: './product-list.component.html',
  styleUrls: ['./product-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ProductListService],
})
export class ProductListComponent {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly productListService = inject(ProductListService);
  private readonly seoService = inject(SeoService);
  private readonly dataLayer = inject(DataLayerService);
  private readonly state = inject(StateService);

  private readonly t = inject(TranslocoService);
  private page = toSignal(
    this.activatedRoute.queryParams.pipe(
      map((params) => parseInt(params['page'] ?? '1')),
    ),
  );
  readonly filtersService = inject(FiltersService);

  dropdownOptions: { name: string; value: SortOption; valueId: number }[] = [
    { name: this.t.translate('Recommended'), value: 'recommended', valueId: 1 },
    { name: this.t.translate('Price ascending'), value: 'price', valueId: 2 },
    { name: this.t.translate('Price descending'), value: '-price', valueId: 3 },
    { name: this.t.translate('Newest'), value: '-created_at', valueId: 4 },
  ];

  placeholders = new Array(PAGE_SIZE_DEFAULT);
  sortDropdownValue: DropdownOption<SortOption>['value'] = SORT_OPTION_DEFAULT;

  // signals always have '| undefined' typing (known issue), this is a temporary workaround
  currentPage = computed(() => this.page() ?? 1);
  productsPage = toSignal(
    this.productListService.products$.pipe(
      tap((productList) => {
        if (productList && productList.meta.category) {
          this.seoService.setForProductList(productList);

          if (productList.meta.category.id === 'search') {
            this.dataLayer.onSearch(this.activatedRoute.snapshot.queryParams['q']);
          } else {
            this.dataLayer.onViewItemList(productList);
          }
        }
      }),
    ),
  );
  isLoading = this.productListService.isLoading;
  isTaxonLoading = this.productListService.isTaxonLoading;
  filters = computed(() => this.productsPage()?.meta.filters ?? EMPTY_FILTERS);
  sorting = computed(() => this.productsPage()?.meta.sorting ?? SORT_OPTION_DEFAULT);
  products = computed(() => this.productsPage()?.products);
  isSearch = toSignal(
    this.activatedRoute.url.pipe(
      map(
        ([segment]) =>
          segment?.path === ROUTE_TRANSLATIONS[this.state.getActiveLocale()]?.search,
      ),
    ),
  );

  sortSub = this.productListService.products$
    .pipe(takeUntilDestroyed())
    .subscribe((v) => {
      this.sortDropdownValue =
        (this.dropdownOptions.find((c) => c.value == v?.meta.sorting)
          ?.value as SortOption) ?? SORT_OPTION_DEFAULT;
    });

  onFiltersChange(option: SortOption): void {
    const sort = option === SORT_OPTION_DEFAULT ? undefined : option;

    void this.router.navigate(['./'], {
      relativeTo: this.activatedRoute,
      queryParams: { ...getSelectedFiltersAsParams(this.filters()), sort },
    });

    this.filtersService.isFiltersOpen.set(false);
  }

  onFiltersClear(): void {
    void this.router.navigate(['./'], { relativeTo: this.activatedRoute });
  }

  onItemSelected(item: ProductListItemData, index: number): void {
    this.dataLayer.onSelectItem(this.productsPage()!.meta.category.name, item, index);
  }

  onSortChange(option?: DropdownOption['value']): void {
    void this.router.navigate([], {
      queryParams: {
        sort: option === SORT_OPTION_DEFAULT ? undefined : option,
      },
      queryParamsHandling: 'merge',
    });
  }

  shouldDisplayHeader(): boolean {
    const productPage = this.productsPage();

    return (
      !!productPage &&
      !this.isTaxonLoading() &&
      !!productPage.meta &&
      !this.isSearch() &&
      !!productPage.meta.category?.image?.url &&
      !!productPage.meta.category?.description
    );
  }
}
